import React, { useCallback, useState } from 'react';
import { Divider } from '@chakra-ui/react';
import { useOutletContext } from 'react-router-dom';
import {
    formatCurrencyValue,
    getFullPaymentAmount,
    getFullPaymentAmountText,
    getNextInstallment,
    getPartialPaymentAmountText
} from 'ts/common/components/invoice/helpers';
import { useTranslate } from 'ts/common/hooks';
import ClientPaymentOptions, {
    type ClientPaymentOption
} from 'client_react/common/components/ClientPaymentOptions';

const MIN_CUSTOM_AMOUNT = 1;

interface Props {
    customAmount: number;
    paymentAmountType: ClientPaymentOption;
    setCustomAmount: (amount: number) => void;
    setPaymentAmountType: (x: ClientPaymentOption) => void;
}

const InvoicePaymentOptions = ({
    customAmount,
    paymentAmountType,
    setCustomAmount,
    setPaymentAmountType
}: Props) => {
    const { invoice } = useOutletContext<{ invoice: SpApi.Client.IInvoice }>();
    const t = useTranslate('clientInvoice');

    const [customAmountError, setCustomAmountError] = useState<string | undefined>(undefined);

    const fullPaymentAmountText = getFullPaymentAmountText(invoice);
    const partialPaymentAmountText = getPartialPaymentAmountText(invoice);

    const fullOptionText = t('payInFull', {
        price: fullPaymentAmountText
    });

    const nextInstallment = getNextInstallment(invoice);

    const nextInstallmentNumber =
        nextInstallment && invoice.installments.indexOf(nextInstallment) + 1;

    const retainerOptionText = t(
        !nextInstallmentNumber
            ? 'paymentOverdue'
            : nextInstallmentNumber === 1 && invoice.invoiceRetainerLabel
            ? 'payRetainer'
            : 'payInstallment',
        {
            installmentNumber: nextInstallmentNumber,
            invoiceRetainerLabel: invoice.invoiceRetainerLabel,
            price: partialPaymentAmountText
        }
    );

    const maxAmount = getFullPaymentAmount(invoice);

    // Show retainer/installment option only when full payment differs from partial payment
    // This means there are multiple unpaid installments remaining
    const showRetainerOption = fullPaymentAmountText !== partialPaymentAmountText;

    const handleCustomAmountBlur = useCallback(() => {
        if (customAmount < MIN_CUSTOM_AMOUNT) {
            setCustomAmountError(
                t('customAmountMinError', {
                    minAmount: formatCurrencyValue(MIN_CUSTOM_AMOUNT, invoice.currencyCode)
                })
            );
        } else if (customAmount > maxAmount) {
            setCustomAmountError(
                t('customAmountMaxError', {
                    maxAmount: formatCurrencyValue(maxAmount, invoice.currencyCode)
                })
            );
        } else {
            setCustomAmountError(undefined);
        }
    }, [customAmount, invoice.currencyCode, maxAmount, t]);

    const handleCustomAmountChange = useCallback(
        (amount: number) => {
            // Clear error when user starts typing again
            if (customAmountError) {
                setCustomAmountError(undefined);
            }
            setCustomAmount(amount);
        },
        [customAmountError, setCustomAmount]
    );

    return (
        <>
            <ClientPaymentOptions
                currencyCode={invoice.currencyCode}
                customAmount={customAmount}
                customAmountError={customAmountError}
                customOptionText={t('customAmount')}
                fullOptionText={fullOptionText}
                onChange={setPaymentAmountType}
                onCustomAmountBlur={handleCustomAmountBlur}
                onCustomAmountChange={handleCustomAmountChange}
                retainerOptionText={retainerOptionText}
                showCustomOption
                showRetainerOption={showRetainerOption}
                title={t('paymentOptions')}
                value={paymentAmountType}
            />
            <Divider />
        </>
    );
};

export default InvoicePaymentOptions;
